// DispatchDeck - Offline Photo Queue
// IndexedDB-backed queue for uploading DVIR photos when connectivity returns

const DB_NAME = 'dispatchdeck-photos';
// TASK-4604 — v2 adds a `scope` index. v1 had NO tenant or owner on an entry, and
// `getPending()` with no argument returns store.getAll() — every pending blob on
// the device, from every tenant and every driver who ever queued one. IndexedDB
// is per-ORIGIN, not per-user, and a yard tablet is shared. See the scoping note
// on enqueue().
const DB_VERSION = 2;
const STORE_NAME = 'pending';
const MAX_QUEUE_SIZE = 20;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      let store;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('pretripId', 'pretripId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      } else {
        store = e.target.transaction.objectStore(STORE_NAME);
      }
      // Index-only upgrade: existing rows are PRESERVED (they simply carry no
      // `scope` and are handled as legacy — see getPending). A driver with
      // pending DVIR photos must not lose them to a version bump.
      if (!store.indexNames.contains('scope')) {
        store.createIndex('scope', 'scope', { unique: false });
      }
      // TASK-4602 — compound [scope, ticketId] so a screen can ask "how many of
      // MY photos are still pending for THIS ticket" with an exact index count
      // and zero blob deserialisation. Folded into the v2 upgrade rather than a
      // v3 because v2 has not shipped yet — no client is on it, so there is
      // nothing to migrate twice.
      //
      // The null-key rule works FOR us here: IndexedDB does not index a record
      // whose key path resolves to null, so DVIR rows (ticketId: null) are
      // excluded from this index by construction. The count cannot accidentally
      // include another feature's photos.
      if (!store.indexNames.contains('scopeTicket')) {
        store.createIndex('scopeTicket', ['scope', 'ticketId'], { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx(db, mode) {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Add a photo to the offline queue
 */
// 🔴 REFUSE an unscoped enqueue. Writing one would create exactly the shared
// bucket this task exists to remove, and it would be invisible until a second
// driver picked up the tablet.
//
// ⚠️ CORRECTION to what I first wrote here: I claimed the caller's catch made
// this a logged failure rather than a lost photo "because the photo is still
// staged in the form". COD checked and that is FALSE — `saveInspection` clears
// `PretripState.pendingPhotos`, so a refused enqueue DOES lose the photo. The
// throw stays, because it guards a real invariant and a scope is always
// resolvable while a driver is active, but the caller now logs it through
// `window.logError` so a refusal is COUNTABLE instead of console-only.
export async function enqueue(entry) {
  if (!entry || !entry.scope) {
    throw new Error('photoQueue.enqueue requires a scope — refusing to queue an unowned blob');
  }
  const db = await openDb();
  const count = await promisify(tx(db, 'readonly').count());
  if (count >= MAX_QUEUE_SIZE) {
    db.close();
    throw new Error(`Offline queue full (max ${MAX_QUEUE_SIZE})`);
  }
  const item = {
    id: entry.id || `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    // TASK-4604 — who this blob belongs to. An entry without a scope can only
    // ever be drained through the legacy ownership check in getPending, never
    // handed to an arbitrary session.
    scope: entry.scope || null,
    pretripId: entry.pretripId || null,
    // TASK-4602 — ticket photos share this queue with DVIR photos. Without these
    // three the item builder would silently DROP them: an entry would come back
    // with a blob and no idea which ticket it belonged to, so the drain could
    // never file it. `pretripId` and `ticketId` are mutually exclusive in
    // practice and each drain filters for its own, so neither owner can touch
    // the other's rows.
    ticketId: entry.ticketId || null,
    name: entry.name || null,
    contentType: entry.contentType || null,
    // TASK-9868 — a ticket photo's type (BOL / scale ticket / …). Without it
    // here the item builder drops the driver's pick on an offline save.
    docType: entry.docType || null,
    // TASK-9868 COD — who queued it, stamped at enqueue so a PIN switch before
    // the drain can never re-attribute the photo to the next driver.
    uploadedBy: entry.uploadedBy || null,
    blob: entry.blob,
    thumbBlob: entry.thumbBlob,
    isDefectPhoto: entry.isDefectPhoto || false,
    createdAt: new Date().toISOString(),
    retryCount: 0
  };
  await promisify(tx(db, 'readwrite').put(item));
  db.close();
  return item.id;
}

/**
 * Get all pending photos, optionally filtered by pretripId
 */
// TASK-4604 — `scope` is REQUIRED. The old signature took an optional pretripId
// and, given nothing, returned store.getAll() — every pending blob on the device
// from every tenant and every driver. On a shared yard tablet that meant the next
// driver to PIN in could read (and dequeue) the previous driver's queued photos,
// and pretrip's drain then uploaded them into whatever tenant was logged in NOW.
// Cross-driver local read AND cross-tenant write, in shipped code.
//
// Callers pass the CURRENT session's scope. Entries queued before v2 carry no
// scope; they are returned ONLY as `legacy: true` so the caller can apply its own
// ownership test (pretrip: does this pretripId exist in MY tenant?) rather than
// being trusted blindly. Dropping them outright would silently bin a driver's
// offline work, which is the failure this queue exists to prevent.
export async function getPending(scope, opts) {
  if (!scope) throw new Error('photoQueue.getPending requires a scope — refusing to return every blob on this device');
  const db = await openDb();
  const store = tx(db, 'readonly');
  const mine = await promisify(store.index('scope').getAll(scope));
  let legacy = [];
  if (opts && opts.includeLegacy) {
    // Cursor, not getAll(). getAll() materialises EVERY blob on the device at
    // once just to find the handful without a scope — with 20 queued photos on a
    // phone that is a real memory spike for a filter (COD). A cursor
    // deserialises one value at a time and we keep only the legacy ones.
    legacy = await new Promise((resolve, reject) => {
      const out = [];
      const req = tx(db, 'readonly').openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return resolve(out);
        if (!cur.value.scope) out.push({ ...cur.value, legacy: true });
        cur.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }
  db.close();
  return mine.concat(legacy);
}

/**
 * Remove a photo from the queue after successful upload
 */
// TASK-4602 — cross-tab claim. Two tabs (or a flapping connection) can drain at
// once and double-upload a photo before either dequeues it. An IndexedDB
// readwrite transaction is the natural lock: claim inside one, and a second
// drainer sees the stamp and skips. Returns false if someone else holds it.
// Claims expire so a tab that dies mid-upload does not strand the entry forever.
export async function claim(id, ttlMs) {
  const ttl = Number(ttlMs) || 5 * 60 * 1000;
  const db = await openDb();
  try {
    const store = tx(db, 'readwrite');
    const item = await promisify(store.get(id));
    if (!item) return false;
    const now = Date.now();
    const held = item.claimedAt ? (now - new Date(item.claimedAt).getTime()) : Infinity;
    if (held < ttl) return false;           // someone else is working it
    item.claimedAt = new Date(now).toISOString();
    await promisify(store.put(item));
    return true;
  } catch (_) {
    return false;
  } finally {
    db.close();
  }
}

export async function dequeue(id) {
  const db = await openDb();
  await promisify(tx(db, 'readwrite').delete(id));
  db.close();
}

/**
 * Increment retry count for a failed upload
 */
export async function incrementRetry(id) {
  const db = await openDb();
  const store = tx(db, 'readwrite');
  const item = await promisify(store.get(id));
  if (item) {
    item.retryCount = (item.retryCount || 0) + 1;
    await promisify(store.put(item));
  }
  db.close();
}

/**
 * Update pretripId for queued photos (assigned after DVIR is saved)
 */
// TASK-4604 — this used to call getPending(oldPretripId) back when that took a
// pretripId. getPending now takes a SCOPE, so the old call was querying the scope
// index for a pretrip id and would have silently matched nothing. Dead today (no
// caller), but a broken exported API is a trap for whoever wires it next (COD).
// Query the pretripId index directly instead.
export async function assignPretripId(oldPretripId, newPretripId) {
  if (!oldPretripId) return;
  const rdb = await openDb();
  const items = await promisify(tx(rdb, 'readonly').index('pretripId').getAll(oldPretripId));
  rdb.close();
  if (items.length === 0) return;
  const db = await openDb();
  const store = tx(db, 'readwrite');
  for (const item of items) {
    item.pretripId = newPretripId;
    await promisify(store.put(item));
  }
  db.close();
}

/**
 * Get count of pending photos
 */
// TASK-4602 — how many of THIS driver's photos are still queued for THIS ticket.
// An index count: no value is deserialised, so the Build Ticket open path can
// call it without pulling blobs into memory on a phone.
//
// 🔴 TASK-4608 — RETURNS null WHEN IT CANNOT TELL, never 0.
// This used to `catch (_) { return 0; }`, which is fine while a failed read only
// means "don't draw the photo banner". It stopped being fine when the banner
// merged: with the row-count also 0, a swallowed IndexedDB error made the caller
// fall through to the affirmative "N entries are saved on the server" — telling a
// driver his work is safe on the strength of a read that never happened
// (COD Code Reviewer + Blast-Radius). A count of 0 and a failure to count are
// different facts and the UI has to be able to tell them apart.
// Still never throws — the caller decides what to show, a banner is not worth
// breaking a screen over.
export async function pendingTicketCount(scope, ticketId) {
  // No scope/ticket is not a failure: nothing could have been queued in the
  // first place (enqueue refuses without a scope), so 0 is the honest answer.
  if (!scope || !ticketId) return 0;
  try {
    const db = await openDb();
    const idx = tx(db, 'readonly').index('scopeTicket');
    const count = await promisify(idx.count([scope, ticketId]));
    db.close();
    return count;
  } catch (_) { return null; }
}

// TASK-4604 — scoped, and it COUNTS rather than materialising blobs (an index
// count never deserialises the value), so a Build Ticket open can call it without
// pulling 20 photos into memory on a phone.
export async function pendingCount(scope) {
  if (!scope) throw new Error('photoQueue.pendingCount requires a scope');
  const db = await openDb();
  const count = await promisify(tx(db, 'readonly').index('scope').count(scope));
  db.close();
  return count;
}

/**
 * Clear all pending photos
 */
export async function clearAll() {
  const db = await openDb();
  await promisify(tx(db, 'readwrite').clear());
  db.close();
}
